import React, { useCallback, useEffect, useState } from 'react';
import { useMatch } from 'react-router-dom';
import { isBefore, isValid } from 'date-fns';
import { isEqual } from 'lodash';
import {
    Drawer,
    Button,
    Divider,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    styled,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers-pro';
import Icon from '@mdi/react';
import { mdiWindowClose } from '@mdi/js';

import { useAppSelector } from '../../state/hooks';
import { privateRoutes } from '../../routes/appRouteList';
import { Filters } from '../../interfaces/filters';
import { formatDateForApi, apiDateToLocalDate } from '../../utils/dateUtils';
import { ApiStatus } from '../../utils/enums';
import { truncateId } from '../../utils/formattingUtils';
import { getFilterValuesFromParams } from '../../utils/stateHelpers';
import SelectAutocomplete from '../SelectAutocomplete/SelectAutocomplete';

const classesPrefix = 'filterDrawer';

const classes = {
    drawer: `${classesPrefix}-drawer`,
    fieldContainer: `${classesPrefix}-fieldContainer`,
    drawerBtns: `${classesPrefix}-drawerBtns`,
    closeIcon: `${classesPrefix}-closeIcon`,
    revenueInputLabel: `${classesPrefix}-revenueInputLabel`,
    revenueTextFields: `${classesPrefix}-revenueTextFields`,
};

const StyledDrawer = styled(Drawer)(({ theme }) => {
    return {
        [`& .${classes.drawer}`]: {
            padding: theme.spacing(3),
            minWidth: '450px',
            maxWidth: '450px',
            [theme.breakpoints.down('sm')]: {
                padding: theme.spacing(1),
                minWidth: 320,
                maxWidth: 320,
            },
        },
        [`& .${classes.fieldContainer}`]: {
            display: 'flex',
            flexDirection: 'inherit',
            alignItems: 'stretch',
        },
        [`& .${classes.drawerBtns}`]: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
        },
        [`& .${classes.closeIcon}`]: {
            padding: theme.spacing(1),
        },
        [`& .${classes.revenueInputLabel}`]: {
            margin: theme.spacing(1),
        },
        [`& .${classes.revenueTextFields}`]: {
            display: 'flex',
            flexDirection: 'row',
            marginTop: theme.spacing(1),
            marginBottom: theme.spacing(1),
        },
    };
});

const FilterDrawer = ({
    initialFilters,
    isOpen,
    onClose,
    resetFilters,
    applyFilters,
    title,
}: {
    initialFilters: Filters;
    isOpen: boolean;
    onClose: () => void;
    resetFilters: () => void;
    applyFilters: (filters: Filters) => void;
    title?: string;
}): React.JSX.Element => {
    const shipperDetailsMatch = useMatch(privateRoutes.shipperDetails);

    const [draftFilters, setDraftFilters] = useState<Filters>({ ...initialFilters });
    const [startDateValue, setStartDateValue] = useState<Date | null>(apiDateToLocalDate(initialFilters.earliest_delivery_departure_timestamp));
    const [endDateValue, setEndDateValue] = useState<Date | null>(apiDateToLocalDate(initialFilters.latest_delivery_departure_timestamp));

    const filterCarrierBusinessUnitsApiStatus = useAppSelector((state) => { return state.filters.filterCarrierBusinessUnitsApiStatus; });
    const filterCommoditiesApiStatus = useAppSelector((state) => { return state.filters.filterCommoditiesApiStatus; });
    const filterConsigneesApiStatus = useAppSelector((state) => { return state.filters.filterConsigneesApiStatus; });
    const filterEquipmentTypesApiStatus = useAppSelector((state) => { return state.filters.filterEquipmentTypesApiStatus; });
    const filterShippersApiStatus = useAppSelector((state) => { return state.filters.filterShippersApiStatus; });

    const filterOptions = useAppSelector((state) => { return state.filters.filterOptions; });

    const clearFilters = useCallback((): void => {
        setDraftFilters({
            ...initialFilters,
        });
        setStartDateValue(apiDateToLocalDate(initialFilters.earliest_delivery_departure_timestamp));
        setEndDateValue(apiDateToLocalDate(initialFilters.latest_delivery_departure_timestamp));
    }, [initialFilters]);

    useEffect(() => {
        clearFilters();
    }, [initialFilters, clearFilters]);

    const setRevenueLimit = (limit: 'min' | 'max', input: string): void => {
        let parsedInput: number | null = parseInt(input, 10);

        if (Number.isNaN(parsedInput)) {
            parsedInput = null;
        }

        setDraftFilters((currentState) => {
            return {
                ...currentState,
                ...limit === 'min' && { minimum_revenue_usd_amount: parsedInput },
                ...limit === 'max' && { maximum_revenue_usd_amount: parsedInput },
            };
        });
    };

    const handleResetClick = (): void => {
        resetFilters();
    };

    const handleCancelClick = (): void => {
        clearFilters();
    };

    const handleApplyClick = (): void => {
        if (startDateValue && endDateValue) {
            const appliedFilters = {
                ...draftFilters,
                earliest_delivery_departure_timestamp: formatDateForApi(startDateValue),
                latest_delivery_departure_timestamp: formatDateForApi(endDateValue),
            };

            applyFilters(appliedFilters);
            onClose();
        }
    };

    /** compares filters stored in state to those in props */
    const filtersHaveChanged = (): boolean => {
        if (!startDateValue || !isValid(startDateValue) || !endDateValue || !isValid(endDateValue)) {
            return true;
        }

        const draftFiltersToCompare: Filters = {
            ...draftFilters,
            earliest_delivery_departure_timestamp: formatDateForApi(startDateValue),
            latest_delivery_departure_timestamp: formatDateForApi(endDateValue),
        };

        return !isEqual(initialFilters, draftFiltersToCompare);
    };

    const maxRevenueErrorMessage = (): string => {
        if (draftFilters.maximum_revenue_usd_amount !== undefined && draftFilters.maximum_revenue_usd_amount !== null &&
            draftFilters.minimum_revenue_usd_amount !== undefined && draftFilters.minimum_revenue_usd_amount !== null &&
            draftFilters.minimum_revenue_usd_amount > draftFilters.maximum_revenue_usd_amount) {
            return 'Max revenue must be greater than min';
        }

        return '';
    };

    const minRevenueErrorMessage = (): string => {
        if (draftFilters.minimum_revenue_usd_amount !== undefined &&
            draftFilters.minimum_revenue_usd_amount !== null &&
            draftFilters.minimum_revenue_usd_amount < 0) {
            return 'Min revenue must be greater than 0';
        }

        if (draftFilters.maximum_revenue_usd_amount &&
            draftFilters.minimum_revenue_usd_amount &&
            draftFilters.maximum_revenue_usd_amount < draftFilters.minimum_revenue_usd_amount) {
            return 'Min revenue must be greater than max';
        }

        return '';
    };

    const isValidDateRange = startDateValue && endDateValue && (isBefore(startDateValue, endDateValue) || isEqual(startDateValue, endDateValue));

    const endDateErrorMessage = (): string => {
        if (!endDateValue) {
            return 'MM/DD/YYYY';
        }

        if (!isValid(endDateValue)) {
            return 'Invalid Date';
        }

        if (!isValidDateRange) {
            return 'End date must be equal to or after start date';
        }

        return '';
    };

    const startDateErrorMessage = (): string => {
        if (!startDateValue) {
            return 'MM/DD/YYYY';
        }

        if (!isValid(startDateValue)) {
            return 'Invalid Date';
        }

        if (!isValidDateRange) {
            return 'Start date must be equal to or before end date';
        }

        return '';
    };

    return (
        <StyledDrawer
            anchor='right'
            open={isOpen}
            classes={{ paper: classes.drawer }}
            onClose={(): void => {
                onClose();
            }}
        >
            <Grid
                container
                direction='column'
                spacing={2}
                sx={{
                    alignContent: 'stretch',
                }}
            >
                <Grid item xs='auto'>
                    <Grid
                        container
                        sx={{
                            alignItems: 'flex-end',
                        }}
                    >
                        <Grid item xs>
                            <Typography variant='h3'>
                                {title || 'Filters'}
                            </Typography>
                        </Grid>
                        <Grid item xs='auto' className={classes.drawerBtns}>
                            <IconButton
                                size='small'
                                className={classes.closeIcon}
                                onClick={(): void => { onClose(); }}
                            >
                                <Icon path={mdiWindowClose} size={1} />
                            </IconButton>
                            <Button
                                size='small'
                                onClick={(): void => {
                                    handleResetClick();
                                    onClose();
                                }}
                            >
                                Reset
                            </Button>
                        </Grid>
                    </Grid>
                    <Divider />
                </Grid>
                <Grid item xs='auto' className={classes.fieldContainer}>
                    <DatePicker
                        label='Start Date'
                        value={startDateValue}
                        onChange={(value): void => {
                            setStartDateValue(value);
                        }}
                        format='MM/dd/yyyy'
                        slotProps={{
                            textField: {
                                variant: 'standard',
                                helperText: startDateErrorMessage(),
                                error: Boolean(startDateErrorMessage()),
                            },
                        }}
                    />
                </Grid>
                <Grid item xs='auto' className={classes.fieldContainer}>
                    <DatePicker
                        label='End Date'
                        value={endDateValue}
                        onChange={(value): void => {
                            setEndDateValue(value);
                        }}
                        format='MM/dd/yyyy'
                        slotProps={{
                            textField: {
                                variant: 'standard',
                                helperText: endDateErrorMessage(),
                                error: Boolean(endDateErrorMessage()),
                            },
                        }}
                    />
                </Grid>
                <Grid item xs='auto' className={classes.fieldContainer}>
                    <SelectAutocomplete
                        label='Business Unit'
                        value={
                            getFilterValuesFromParams(
                                filterOptions.carrierBusinessUnits,
                                'carrier_business_unit_code',
                                draftFilters.carrier_business_unit_codes
                            )
                        }
                        options={filterOptions.carrierBusinessUnits}
                        loading={filterCarrierBusinessUnitsApiStatus === ApiStatus.Idle || filterCarrierBusinessUnitsApiStatus === ApiStatus.Loading}
                        multiple
                        getOptionLabel={(option): string => {
                            const formattedcode = option?.carrier_business_unit_code?.replaceAll(
                                /\s/g,
                                '\u00B7'
                            ) || '';

                            return `${option.carrier_business_unit_name.trim() || ''} (${formattedcode || ''})`;
                        }}
                        isOptionEqualToValue={(option, value): boolean => {
                            return option.carrier_business_unit_code === value.carrier_business_unit_code;
                        }}
                        formatChipContent={(option): string => {
                            return option.carrier_business_unit_name;
                        }}
                        onChange={(event, value): void => {
                            const carrierBusinessUnitCodes = value.map((carrierBusinessUnitCode) => {
                                return carrierBusinessUnitCode.carrier_business_unit_code;
                            });
                            setDraftFilters((currentState) => {
                                return {
                                    ...currentState,
                                    carrier_business_unit_codes: carrierBusinessUnitCodes,
                                };
                            });
                        }}
                    />
                </Grid>
                <Grid item xs='auto' className={classes.fieldContainer}>
                    <SelectAutocomplete
                        label='Equipment Types'
                        value={
                            getFilterValuesFromParams(
                                filterOptions.equipmentTypes,
                                'freight_order_specified_eqpt_code',
                                draftFilters.freight_order_specified_eqpt_codes
                            )
                        }
                        options={filterOptions.equipmentTypes}
                        loading={filterEquipmentTypesApiStatus === ApiStatus.Idle || filterEquipmentTypesApiStatus === ApiStatus.Loading}
                        multiple
                        getOptionLabel={(option): string => {
                            return option.freight_order_specified_eqpt_code || '';
                        }}
                        isOptionEqualToValue={(option, value): boolean => {
                            return option.freight_order_specified_eqpt_code === value.freight_order_specified_eqpt_code;
                        }}
                        formatChipContent={(option): string => {
                            return option.freight_order_specified_eqpt_code;
                        }}
                        onChange={(event, value): void => {
                            const freightOrderSpecifiedEqptCodes = value.map((equipmentCode) => {
                                return equipmentCode.freight_order_specified_eqpt_code;
                            });
                            setDraftFilters((currentState) => {
                                return {
                                    ...currentState,
                                    freight_order_specified_eqpt_codes: freightOrderSpecifiedEqptCodes,
                                };
                            });
                        }}
                    />
                </Grid>
                <Grid item xs='auto' className={classes.fieldContainer}>
                    <SelectAutocomplete
                        label='Shippers'
                        value={
                            shipperDetailsMatch !== null ?
                                filterOptions.shippers.filter((shipper) => {
                                    return shipper.pickup_party_id === shipperDetailsMatch?.params.id;
                                }) :
                                getFilterValuesFromParams(
                                    filterOptions.shippers,
                                    'pickup_party_id',
                                    draftFilters.pickup_party_ids
                                )
                        }
                        options={filterOptions.shippers}
                        loading={filterShippersApiStatus === ApiStatus.Idle || filterShippersApiStatus === ApiStatus.Loading}
                        multiple
                        getOptionLabel={(option): string => {
                            return `${option.pickup_party_name.trim() || ''} (${option.pickup_party_id.trim() || ''})`;
                        }}
                        isOptionEqualToValue={(option, value): boolean => {
                            return option.pickup_party_id === value.pickup_party_id;
                        }}
                        formatChipContent={(option): string => {
                            return truncateId(option.pickup_party_name);
                        }}
                        onChange={(event, value): void => {
                            const pickupPartyIds = value.map((pickupParty) => {
                                return pickupParty.pickup_party_id;
                            });
                            setDraftFilters((currentState) => {
                                return {
                                    ...currentState,
                                    pickup_party_ids: pickupPartyIds,
                                };
                            });
                        }}
                        disabled={shipperDetailsMatch !== null}
                    />
                </Grid>
                <Grid item xs='auto' className={classes.fieldContainer}>
                    <SelectAutocomplete
                        label='Consignees'
                        value={
                            getFilterValuesFromParams(
                                filterOptions.consignees,
                                'delivery_party_id',
                                draftFilters.delivery_party_ids
                            )
                        }
                        options={filterOptions.consignees}
                        loading={filterConsigneesApiStatus === ApiStatus.Idle || filterConsigneesApiStatus === ApiStatus.Loading}
                        multiple
                        getOptionLabel={(option): string => {
                            return `${option.delivery_party_name.trim() || ''} (${option.delivery_party_id.trim() || ''})`;
                        }}
                        isOptionEqualToValue={(option, value): boolean => {
                            return option.delivery_party_id === value.delivery_party_id;
                        }}
                        formatChipContent={(option): string => {
                            return truncateId(option.delivery_party_name);
                        }}
                        onChange={(event, value): void => {
                            const deliveryPartyIds = value.map((deliveryParty) => {
                                return deliveryParty.delivery_party_id;
                            });
                            setDraftFilters((currentState) => {
                                return {
                                    ...currentState,
                                    delivery_party_ids: deliveryPartyIds,
                                };
                            });
                        }}
                    />
                </Grid>
                <Grid item xs='auto' className={classes.fieldContainer}>
                    <SelectAutocomplete
                        label='Commodities'
                        value={
                            getFilterValuesFromParams(
                                filterOptions.commodities,
                                'commodity_code',
                                draftFilters.commodity_codes
                            )
                        }
                        options={filterOptions.commodities}
                        loading={filterCommoditiesApiStatus === ApiStatus.Idle || filterCommoditiesApiStatus === ApiStatus.Loading}
                        multiple
                        getOptionLabel={(option): string => {
                            return option?.commodity_code?.replaceAll(
                                /\s/g,
                                '\u00B7'
                            ) || '';
                        }}
                        isOptionEqualToValue={(option, value): boolean => {
                            return option.commodity_code === value.commodity_code;
                        }}
                        formatChipContent={(option): string => {
                            return option.commodity_code;
                        }}
                        onChange={(event, value): void => {
                            const commidityCodes = value.map((commodity) => {
                                return commodity.commodity_code;
                            });
                            setDraftFilters((currentState) => {
                                return {
                                    ...currentState,
                                    commodity_codes: commidityCodes,
                                };
                            });
                        }}
                    />
                </Grid>
                <Grid item xs='auto' className={classes.fieldContainer}>
                    <InputLabel className={classes.revenueInputLabel}>
                        <span>Revenue Range </span>
                        <span><small>(USD)</small></span>
                    </InputLabel>
                    <div className={classes.revenueTextFields}>
                        <TextField
                            label='Min. Revenue'
                            type='number'
                            fullWidth
                            variant='standard'
                            value={draftFilters.minimum_revenue_usd_amount ?? ''}
                            onChange={(e): void => {
                                setRevenueLimit('min', e.target.value);
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position='start'>$</InputAdornment>
                                ),
                            }}
                            inputProps={{
                                max: draftFilters.maximum_revenue_usd_amount,
                                min: 0,
                            }}
                            error={Boolean(minRevenueErrorMessage())}
                            helperText={minRevenueErrorMessage()}
                        />
                        <TextField
                            label='Max. Revenue'
                            type='number'
                            fullWidth
                            variant='standard'
                            value={draftFilters.maximum_revenue_usd_amount ?? ''}
                            onChange={(e): void => {
                                setRevenueLimit('max', e.target.value);
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position='start'>
                                        $
                                    </InputAdornment>
                                ),
                            }}
                            inputProps={{
                                min: draftFilters.minimum_revenue_usd_amount,
                            }}
                            error={Boolean(maxRevenueErrorMessage())}
                            helperText={maxRevenueErrorMessage()}
                        />
                    </div>
                </Grid>
                <Divider />
                <Grid item xs='auto'>
                    <Grid
                        container
                        spacing={1}
                        sx={{
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                        }}
                    >
                        <Grid item>
                            <Button
                                onClick={handleCancelClick}
                            >
                                Cancel
                            </Button>
                        </Grid>
                        <Grid item>
                            <Tooltip title='Apply Filters'>
                                <span>
                                    <Button
                                        color='primary'
                                        variant='contained'
                                        disabled={
                                            !filtersHaveChanged() ||
                                            Boolean(startDateErrorMessage()) ||
                                            Boolean(endDateErrorMessage()) ||
                                            Boolean(maxRevenueErrorMessage()) ||
                                            Boolean(minRevenueErrorMessage())
                                        }
                                        onClick={handleApplyClick}
                                    >
                                        Apply
                                    </Button>
                                </span>
                            </Tooltip>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </StyledDrawer>
    );
};

export default FilterDrawer;
